import loadArcGIS from './utils/loadArcGIS';
import { addLegendStylesAfterRender, customViewMapStyles } from './utils/customViewMapStyles';
import { search } from './mapSearch';
import { buildCards, updateVisibleCards } from '../ProjectCard';
import { resetLayerSelection } from './utils/projectLayers';

/**
 * The main class that bind all together
 */
class InteractiveMap {
  constructor(options = {}) {
    this.containerId = 'viewDiv';
    this.apiKey = options.element.dataset.apiKey;
    this.options = Object.assign(
      options,
    );
    this.searchParent = document.getElementById('js-project-search-from');
    this.hasSearchTermResults = false;
  }

  async init() {
    try {
      await loadArcGIS();
      await this.initMap();
    } catch (loadError) {
      console.error('Fehler beim Laden der ArcGIS API:', loadError);
    }
  }

  /**
   * Init map by import modules and executes createMap method
   *
   * @returns {Promise<void>}
   */
  async initMap() {
    if (!window.require) {
      console.error('ArcGIS API wurde nicht geladen.');
      return;
    }

    window.require(
      [
        'esri/WebMap',
        'esri/views/MapView',
        'esri/widgets/Home',
        'esri/geometry/Extent',
        'esri/widgets/Legend',
        'esri/widgets/Expand',
      ],
      (WebMap, MapView, Home, Extent, Legend, Expand) => {
        this.WebMap = WebMap;
        this.MapView = MapView;
        this.Home = Home;
        this.Extent = Extent;
        this.Legend = Legend;
        this.Expand = Expand;
        this.createMap();
      },
    );
  }

  /**
   * Build the view map and parts of widgets (Home, zoom and legend)
   *
   * @returns {Promise<void>}
   */
  async createMap() {
    let stationaryTimeout;

    // Set the initial (reset) extent of the view (middle of germany)
    const germanyExtent = new this.Extent({
      xmin: 652000,
      ymin: 5980000,
      xmax: 1670000,
      ymax: 7360000,
      spatialReference: { wkid: 3857 },
    });

    // Set the reference api key to amprion arcgis map data
    const webMap = new this.WebMap({
      portalItem: {
        id: this.apiKey,
      },
    });

    // Create the view with given options and restrictions
    const view = new this.MapView({
      container: this.containerId,
      map: webMap,
      center: [7.9, 51],
      zoom: 5,
      constraints: {
        geometry: germanyExtent,
        minZoom: 5,
        maxZoom: 16,
        snapToZoom: true,
        minScale: 9000,
        rotationEnable: false,
      },
      interactionOptions: {
        zoom: {
          duration: 0,
          inertia: false,
        },
        drag: {
          inertia: false,
        },
      },
      ...this.options,
    });

    // prevent hit-test on events
    view.popup.autoOpenEnabled = false;
    const block = evt => evt.stopPropagation();
    view.on('pointer-move', block);
    view.on('click', block);
    view.on('double-click', block);
    view.on('pointer-down', block);
    view.on('pointer-up', block);

    // Home button widget in view
    const home = new this.Home({
      view,
      viewpoint: {
        center: [7.9, 51],
        zoom: 5,
        spatialReference: { wkid: 3857 },
      },
    });

    view.ui.add(home, 'top-right');
    home.id = 'home';

    // Reset card list on home-button click when home is ready
    home.when(() => {
      const btn = home.container.querySelector('calcite-button');
      btn.addEventListener('click', () => {
        /* eslint-disable no-restricted-globals */
        setTimeout(() => {
          const cards = document.querySelectorAll('.js-mol-project-card');
          cards.forEach((card) => {
            card.classList.add('is-visible');
            card.classList.remove('is-active');
          });
          resetLayerSelection(webMap);
        }, 300);
      });
    });

    // Move the zoom-widget to the right
    view.ui.move('zoom', 'top-right');

    /**
     * On/when view is loaded
     */
    view.when(async () => {
      // Wait for DOM to init search
      /* eslint-disable no-restricted-globals */
      requestAnimationFrame(() => {
        search(view, webMap, germanyExtent);
      });

      // Initialize project cards, add custom styles for widgets adjust legend widget
      await this.initProjectCards(webMap, view);
      customViewMapStyles(view);

      // Build custom legend widget
      const legend = new this.Legend({ view });
      const legendExpand = new this.Expand({
        view,
        content: legend,
        expandTooltip: 'Legende',
        expandIcon: 'legend',
        collapseIcon: 'chevrons-right',
        mode: 'floating',
      });

      view.ui.add(legendExpand, 'top-right');
      legendExpand.id = 'legend';

      // Add custom legend container style (custom height, width and background color)
      addLegendStylesAfterRender(view);
    });

    /**
     * Function used to prevent too many failure requests, by just stopping requesting
     * - Background: by to many interactions within the view, arcgis fires too many requests, that can resolve in error responses
     */
    const originalFetch = window.fetch;
    let throttleRequests = false;
    window.fetch = (resource, options) => {
      if (throttleRequests) {
        return new Promise((resolve) => {
          /* eslint-disable */
          setTimeout(() => {
            resolve(originalFetch(resource, options));
          }, 350);
          /* eslint-enable */
        });
      }

      return originalFetch(resource, options);
    };

    /**
     * Watchers on events
     */
    view.watch('updating', (updating) => {
      // After updating
      if (!updating) {
        // remove loading from map wrapper element
        const mapWrapper = document.querySelector('#js-org-interactive-map__map-wrapper');
        mapWrapper.classList.remove('loading');
      }
    });

    // Check extent change is done
    view.watch('extent', () => {
      // Check if term search has result - if not then reset visible cards
      this.searchParent.addEventListener('term-changed', (event) => {
        this.hasSearchTermResults = event.detail;

        if (!this.hasSearchTermResults) {
          resetLayerSelection(webMap);
        }
      });

      if (!this.hasSearchTermResults) {
        this.getVisibleBookmarks(view).then((results) => {
          updateVisibleCards(results);
        });
      }

      /* eslint-disable */
      clearTimeout(stationaryTimeout);
      stationaryTimeout = setTimeout(() => {

        // enable request again after throttling
        throttleRequests = false;
        view.map.layers.forEach(layer => {

          // make layers visible after changes are done
          if (!layer.visible) {
            layer.visible = true;
          }

          // re‑fetch layer data after movements
          if (layer.refresh) {
            layer.refresh();
          }
        });
      }, 750);
      /* eslint-enable */
    });

    // Check zooms - throttle requests because the zoom executes to many requests
    view.watch('zoom', (zoom) => {
      if (zoom) {
        // hide layers on zoom to prevent to many requests to ESRI api
        view.map.layers.forEach((layer) => {
          /* eslint-disable no-param-reassign */
          layer.visible = false;
        });

        // disable requests to prvent too many actions to api, this minimizes request errors
        throttleRequests = true;
      }
    });

    // Check rotation
    view.watch('rotation', (value) => {
      // Prevent rotation in view map
      if (value !== 0) {
        view.rotation = 0;
      }
    });

    // Check Scale for changes
    view.watch('scale', (scale) => {
      // break if view scale is under 9000 -> set to min 9000
      if (scale < 9000) {
        view.scale = 9000;
      }
    });
  }

  /**
   * initialize project cards fill in
   *
   * @param map
   * @param view
   * @returns {Promise<void>}
   */
  async initProjectCards(map, view) {
    if (map && view) {
      await buildCards(map, view);
    }
  }

  /**
   * Create Update on project cards depending on given bookmarks in the current EXTENT
   * - if you move the extent of the card, you can see project cards change the result list of current bookmarks in view
   * - this RESULT will later pass to the updateCardsVisibility method to show or hide project cards
   *
   * @param view
   * @returns {Promise<{id: string, name: string, visible: boolean}[]>}
   */
  async getVisibleBookmarks(view) {
    const extent = view.extent;
    const currentExtent = this.shrinkExtent(extent);

    const bookmarks = (view.map.bookmarks && view.map.bookmarks.items) ?
      view.map.bookmarks.items : [];

    // find visible bookmarks
    const visibleBookmarks = bookmarks.filter((bm) => {
      if (!bm.viewpoint || !bm.viewpoint.targetGeometry) {
        return false;
      }

      const geom = bm.viewpoint.targetGeometry;
      let bmExtent = null;

      if (geom && geom.extent) {
        bmExtent = geom.extent || geom;
      }

      if (!bmExtent) {
        return false;
      }

      // the intersect checks raw, if another extent, cross this geo extent from view
      return this.fastIntersects(bmExtent, currentExtent);
    });

    // collect all layers
    const allLayers = Array.from(
      view.map.layers.items.map(layer => ({
        id: layer.id,
        title: layer.title,
      })),
    );

    // results into bookmarks with same name like group layer id
    return allLayers.map((layer) => {
      const match = !!visibleBookmarks.find(bm => bm.name === layer.id);

      return {
        id: layer.id,
        name: layer.title,
        visible: match,
      };
    });
  }

  /**
   * Minimize the extent, that matter - needs to be smaller than 100%
   *
   * @param extent - given extent
   * @param factor - extent factor 0 to 1 (1 = 100%)
   * @returns {*} - Object from Extent
   */
  shrinkExtent(extent, factor = 0.8) {
    const dx = (extent.width * (1 - factor)) / 2;
    const dy = (extent.height * (1 - factor)) / 2;

    /* eslint-disable */
    return new this.Extent({
      xmin: extent.xmin + dx,
      xmax: extent.xmax - dx,
      ymin: extent.ymin + dy,
      ymax: extent.ymax - dy,
      spatialReference: extent.spatialReference,
    });
    /* eslint-enable */
  }

  /**
   * This function simply checks if extents (view-extent vs. bookmark-extent) have intersections
   *
   * @param a - extent one
   * @param b - extent two
   * @returns {boolean} - returns if intersects or not
   */
  fastIntersects(a, b) {
    return !(
      a.xmax < b.xmin ||
      a.xmin > b.xmax ||
      a.ymax < b.ymin ||
      a.ymin > b.ymax
    );
  }
}

export default InteractiveMap;
